import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Pencil, Plus, RefreshCw, Trash2, Search, Image as ImageIcon } from 'lucide-react';
import { vendorApi } from '../../api/vendorApi';
import { formatNumber, formatToman } from '@/features/reports/lib/format';
import { persianDisplayText } from '@/lib/persianUi';

export default function VendorProducts() {
  const [products, setProducts] = useState([]);
  const [state, setState] = useState('loading');
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  const load = async () => {
    setState('loading');
    setError('');
    try {
      setProducts((await vendorApi.getProducts()) ?? []);
      setState('ready');
    } catch (requestError) {
      console.error(requestError);
      setError('خطا در دریافت محصولات');
      setState('error');
    }
  };

  useEffect(() => {
    let cancelled = false;
    vendorApi.getProducts()
      .then((result) => {
        if (cancelled) return;
        setProducts(result ?? []);
        setState('ready');
      })
      .catch((requestError) => {
        if (cancelled) return;
        console.error(requestError);
        setError('خطا در دریافت محصولات');
        setState('error');
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const removeProduct = async (product) => {
    if (!window.confirm(`حذف محصول ${persianDisplayText(product.name_fa, 'نام ثبت‌نشده')}؟`)) return;
    try {
      await vendorApi.deleteProduct(product.id);
      await load();
    } catch (requestError) {
      console.error(requestError);
      setError('حذف محصول با خطا مواجه شد');
    }
  };

  const filteredProducts = useMemo(() => {
    return products.filter(p => {
      const term = searchQuery.toLowerCase();
      const nameFa = (p.name_fa || '').toLowerCase();
      const nameEn = (p.name_en || '').toLowerCase();
      const brand = (p.brand || '').toLowerCase();
      return nameFa.includes(term) || nameEn.includes(term) || brand.includes(term);
    });
  }, [products, searchQuery]);

  return (
      <div style={{ direction: 'rtl', width: '100%', display: 'flex', flexDirection: 'column', gap: '1rem' }}>

        {/* هدر صفحه */}
        <div className="admin-page-header" style={{ marginBottom: 0 }}>
          <div>
            <h1 className="admin-page-title">محصولات</h1>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button onClick={load} className="admin-refresh-btn" disabled={state === 'loading'} style={{ background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1', padding: '0.5rem 1rem' }}>
              <RefreshCw size={16} /><span>بروزرسانی</span>
            </button>
            <button onClick={() => navigate('/vendor/products/add')} className="admin-refresh-btn" style={{ background: '#2563eb', color: '#fff', padding: '0.5rem 1rem' }}>
              <Plus size={16} /><span>محصول جدید</span>
            </button>
          </div>
        </div>

        {error && <div className="admin-table-error"><AlertCircle size={16} />{error}</div>}

        {/* نوار ابزار (جستجوی جمع‌وجور) */}
        <div style={{ display: 'flex', background: '#fff', padding: '0.5rem', borderRadius: '8px', border: '1px solid #e5e7eb', width: 'fit-content' }}>
          <div style={{ position: 'relative', width: '300px' }}>
            <Search size={16} color="#94a3b8" style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }} />
            <input
                type="text"
                placeholder="جستجوی محصول..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ width: '100%', padding: '0.4rem 2rem 0.4rem 0.75rem', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '0.85rem' }}
            />
          </div>
        </div>

        {/* جدول محصولات (فشرده) */}
        <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'right', whiteSpace: 'nowrap' }}>
              <thead style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
              <tr>
                <th style={{ padding: '0.6rem 0.8rem', color: '#475569', fontWeight: 'bold', fontSize: '0.85rem' }}>محصول</th>
                <th style={{ padding: '0.6rem 0.8rem', color: '#475569', fontWeight: 'bold', fontSize: '0.85rem' }}>برند</th>
                <th style={{ padding: '0.6rem 0.8rem', color: '#475569', fontWeight: 'bold', fontSize: '0.85rem' }}>قیمت پایه</th>
                <th style={{ padding: '0.6rem 0.8rem', color: '#475569', fontWeight: 'bold', fontSize: '0.85rem' }}>قیمت تمام‌شده</th>
                <th style={{ padding: '0.6rem 0.8rem', color: '#475569', fontWeight: 'bold', fontSize: '0.85rem' }}>موجودی</th>
                <th style={{ padding: '0.6rem 0.8rem', color: '#475569', fontWeight: 'bold', fontSize: '0.85rem', textAlign: 'center' }}>عملیات</th>
              </tr>
              </thead>
              <tbody>
              {state === 'loading' ? (
                  <tr><td colSpan="6" style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>در حال دریافت اطلاعات...</td></tr>
              ) : filteredProducts.length ? (
                  filteredProducts.map((product) => (
                      <tr key={product.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '0.6rem 0.8rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          {product.image_url ? (
                              <img src={product.image_url} alt={product.name_fa} style={{ width: '32px', height: '32px', borderRadius: '6px', objectFit: 'cover', border: '1px solid #e2e8f0' }} />
                          ) : (
                              <div style={{ width: '32px', height: '32px', borderRadius: '6px', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #e2e8f0' }}>
                                <ImageIcon size={16} color="#94a3b8" />
                              </div>
                          )}
                          <span style={{ fontWeight: 'bold', color: '#1e293b', fontSize: '0.85rem' }}>{persianDisplayText(product.name_fa, 'نام ثبت‌نشده')}</span>
                        </td>
                        <td style={{ padding: '0.6rem 0.8rem', color: '#64748b', fontSize: '0.85rem' }}>{product.brand?.trim() || '—'}</td>
                        <td style={{ padding: '0.6rem 0.8rem', color: '#1e293b', fontSize: '0.85rem' }}>
                          {product.base_price == null ? '—' : formatToman(product.base_price)}
                          {product.wholesale_price != null && (
                              <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '0.1rem' }}>
                                عمده: {formatToman(product.wholesale_price)} (حداقل {formatNumber(product.wholesale_min_quantity)})
                              </div>
                          )}
                        </td>
                        <td style={{ padding: '0.6rem 0.8rem', color: '#64748b', fontSize: '0.85rem' }}>
                          {product.cost_price == null ? '—' : formatToman(product.cost_price)}
                        </td>
                        <td style={{ padding: '0.6rem 0.8rem' }}>
                      <span style={{
                        padding: '0.2rem 0.5rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 'bold',
                        background: product.stock_quantity > 0 ? '#dcfce7' : '#fee2e2',
                        color: product.stock_quantity > 0 ? '#15803d' : '#b91c1c'
                      }}>
                        {formatNumber(product.stock_quantity ?? 0)} عدد
                      </span>
                        </td>
                        <td style={{ padding: '0.6rem 0.8rem' }}>
                          <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'center' }}>
                            <button onClick={() => navigate(`/vendor/products/edit/${product.id}`)} style={{ background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', padding: '0.3rem 0.5rem', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.2rem', fontSize: '0.75rem', fontWeight: 'bold' }}>
                              <Pencil size={12} /> ویرایش
                            </button>
                            <button onClick={() => removeProduct(product)} style={{ background: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca', padding: '0.3rem 0.5rem', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.2rem', fontSize: '0.75rem' }}>
                              <Trash2 size={12} /> حذف
                            </button>
                          </div>
                        </td>
                      </tr>
                  ))
              ) : (
                  <tr><td colSpan="6" style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>هیچ محصولی یافت نشد.</td></tr>
              )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
  );
}